import { Box, Button, Card, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { upsertMyDoctorProfile } from "../../services/doctors.service";
import { toast } from "sonner";
import { setUserInfo } from "../../store/userInfoSlice";

interface DoctorLocalForm {
  id?: string;
  localName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

const emptyDoctorLocal: DoctorLocalForm = {
  localName: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
};

export function DoctorProfileForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.userInfo);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [doctorCrm, setDoctorCrm] = useState("");
  const [doctorRqe, setDoctorRqe] = useState("");
  const [doctorSpecialty, setDoctorSpecialty] = useState("");
  const [profileAddress, setProfileAddress] = useState("");
  const [profileCity, setProfileCity] = useState("");
  const [profileState, setProfileState] = useState("");
  const [profileZipCode, setProfileZipCode] = useState("");
  const [profileBirthDay, setProfileBirthDay] = useState("");
  const [profileRegistrationNumber, setProfileRegistrationNumber] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [doctorLocals, setDoctorLocals] = useState<DoctorLocalForm[]>([emptyDoctorLocal]);
  const [isSaving, setIsSaving] = useState(false);
  const removeMask = (value: string) => value.replace(/\D/g, "");

  const applyMask = (value: string, maskType: "cpf" | "phone" | "cep") => {
    const numbers = value.replace(/\D/g, "");

    if (maskType === "cpf") {
      return numbers
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    if (maskType === "phone") {
      return numbers
        .slice(0, 11)
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    }

    return numbers
      .slice(0, 8)
      .replace(/(\d{5})(\d)/, "$1-$2");
  };
  // Prefill com dados existentes quando houver
  useEffect(() => {
    setName(user.name || "");
    setEmail(user.email || "");
    setProfileAddress(user.profile?.address || "");
    setProfileCity(user.profile?.city || "");
    setProfileState(user.profile?.state || "");
    setProfileZipCode(user.profile?.zip_code ? applyMask(user.profile.zip_code, "cep") : "");
    setProfileBirthDay(user.profile?.birthDay || "");
    setProfileRegistrationNumber(
      user.profile?.registrationNumber
        ? applyMask(user.profile.registrationNumber, "cpf")
        : ""
    );
    setProfilePhone(user.profile?.phone ? applyMask(user.profile.phone, "phone") : "");
    const doctor = user.doctor;
    if (doctor) {
      setDoctorCrm(doctor.CRM || "");
      setDoctorRqe(doctor.RQE || "");
      setDoctorSpecialty(doctor.specialty || "");

      if (doctor.locals && doctor.locals.length > 0) {
        setDoctorLocals(
          doctor.locals.map((loc) => ({
            id: loc.id,
            localName: loc.localName || "",
            address: loc.address || "",
            city: loc.city || "",
            state: loc.state || "",
            zip: loc.zip_code || "",
            phone: loc.phone || "",
          }))
        );
      } else {
        setDoctorLocals([emptyDoctorLocal]);
      }
    }
  }, [user]);

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Nome e e-mail são obrigatórios.");
      return;
    }
    setIsSaving(true);

    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        crm: doctorCrm || undefined,
        rqe: doctorRqe || undefined,
        specialty: doctorSpecialty || undefined,
        profile: {
          address: profileAddress || undefined,
          city: profileCity || undefined,
          state: profileState || undefined,
          zip_code: profileZipCode ? removeMask(profileZipCode) : undefined,
          birthDay: profileBirthDay || undefined,
          registrationNumber: profileRegistrationNumber
            ? removeMask(profileRegistrationNumber)
            : undefined,
          phone: profilePhone ? removeMask(profilePhone) : undefined,
        },
        locals: doctorLocals
          .filter((loc) =>
            [loc.localName, loc.address, loc.city, loc.state, loc.zip, loc.phone].some(
              (v) => v && v.trim() !== ""
            )
          )
          .map((loc) => ({
            id: loc.id,
            localName: loc.localName || undefined,
            address: loc.address || undefined,
            city: loc.city || undefined,
            state: loc.state || undefined,
            zip_code: loc.zip || undefined,
            phone: loc.phone || undefined,
          })),
      };
      const updated = await upsertMyDoctorProfile(payload);

      dispatch(
        setUserInfo({
          ...user,
          ...(updated?.name ? { name: updated.name } : {}),
          ...(updated?.email ? { email: updated.email } : {}),
          ...(updated?.profile ? { profile: updated.profile } : {}),
          ...(updated?.doctor ? { doctor: updated.doctor } : {}),
        })
      );

      toast.success("Perfil do médico atualizado com sucesso!");
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Não foi possível salvar o perfil do médico. Tente novamente.";
      toast.error(Array.isArray(message) ? String(message[0]) : String(message));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Card
        sx={{
          padding: 4,
          maxWidth: 900,
          width: "100%",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#505152ff" mb={1}>
          Editar perfil do médico
        </Typography>
        <Typography variant="body2" color="#8d8f92ff" mb={3}>
          Atualize seus dados básicos e dados profissionais.
        </Typography>

        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            Dados básicos
          </Typography>
          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="CPF"
              value={profileRegistrationNumber}
              onChange={(e) => setProfileRegistrationNumber(applyMask(e.target.value, "cpf"))}
              fullWidth
            />
            <TextField
              label="Telefone"
              value={profilePhone}
              onChange={(e) => setProfilePhone(applyMask(e.target.value, "phone"))}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Data de nascimento"
              type="date"
              value={profileBirthDay}
              onChange={(e) => setProfileBirthDay(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="CEP"
              value={profileZipCode}
              onChange={(e) => setProfileZipCode(applyMask(e.target.value, "cep"))}
              fullWidth
            />
          </Stack>
          <TextField
            label="Endereço"
            value={profileAddress}
            onChange={(e) => setProfileAddress(e.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Cidade"
              value={profileCity}
              onChange={(e) => setProfileCity(e.target.value)}
              fullWidth
            />
            <TextField
              label="Estado"
              value={profileState}
              onChange={(e) => setProfileState(e.target.value)}
              fullWidth
            />
          </Stack>

          <Typography variant="subtitle2" color="text.secondary" mt={2}>
            Dados profissionais
          </Typography>
          <TextField
            label="CRM"
            value={doctorCrm}
            onChange={(e) => setDoctorCrm(e.target.value)}
            fullWidth
          />
          <TextField
            label="RQE"
            value={doctorRqe}
            onChange={(e) => setDoctorRqe(e.target.value)}
            fullWidth
          />
          <TextField
            label="Especialidade"
            value={doctorSpecialty}
            onChange={(e) => setDoctorSpecialty(e.target.value)}
            fullWidth
          />

          <Typography variant="subtitle2" color="text.secondary" mt={2}>
            Locais de atendimento
          </Typography>

          {doctorLocals.map((local, index) => (
            <Stack
              key={local.id || index}
              spacing={1.5}
              sx={{ p: 1.5, borderRadius: 1, border: "1px solid #eee" }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {doctorLocals.length > 1 ? `Local ${index + 1}` : "Local"}
              </Typography>

              <TextField
                label="Nome do local (clínica, consultório, etc.)"
                value={local.localName}
                onChange={(e) => {
                  const next = [...doctorLocals];
                  next[index] = { ...next[index], localName: e.target.value };
                  setDoctorLocals(next);
                }}
                fullWidth
              />

              <TextField
                label="Endereço do local"
                value={local.address}
                onChange={(e) => {
                  const next = [...doctorLocals];
                  next[index] = { ...next[index], address: e.target.value };
                  setDoctorLocals(next);
                }}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Cidade"
                  value={local.city}
                  onChange={(e) => {
                    const next = [...doctorLocals];
                    next[index] = { ...next[index], city: e.target.value };
                    setDoctorLocals(next);
                  }}
                  fullWidth
                />
                <TextField
                  label="Estado"
                  value={local.state}
                  onChange={(e) => {
                    const next = [...doctorLocals];
                    next[index] = { ...next[index], state: e.target.value };
                    setDoctorLocals(next);
                  }}
                  fullWidth
                />
                <TextField
                  label="CEP"
                  value={local.zip}
                  onChange={(e) => {
                    const next = [...doctorLocals];
                    next[index] = { ...next[index], zip: e.target.value };
                    setDoctorLocals(next);
                  }}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Telefone do local"
                value={local.phone}
                onChange={(e) => {
                  const next = [...doctorLocals];
                  next[index] = { ...next[index], phone: e.target.value };
                  setDoctorLocals(next);
                }}
                fullWidth
              />

              {doctorLocals.length > 1 && (
                <Button
                  variant="text"
                  color="error"
                  onClick={() => {
                    setDoctorLocals((prev) => prev.filter((_, i) => i !== index));
                  }}
                  sx={{ alignSelf: "flex-end" }}
                >
                  Remover local
                </Button>
              )}
            </Stack>
          ))}

          <Button
            variant="outlined"
            onClick={() => setDoctorLocals((prev) => [...prev, emptyDoctorLocal])}
            sx={{ alignSelf: "flex-start" }}
          >
            Adicionar local
          </Button>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Salvando..." : "Salvar perfil do médico"}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
